export const WORKSPACE_ROOT_KEYS = [
  "mySpace",
  "people",
  "time",
  "payroll",
  "insights",
  "admin",
];

const TOP_LEVEL_NAV_ORDER = [
  "dashboard",
  "myspace",
  "people",
  "hiring",
  "time",
  "payroll",
  "performance",
  "insights",
  "whatsnew",
  "admin",
];

const SCOPED_REPORT_NAV_ITEMS = {
  people: { path: "people/reports", sort_order: 9991 },
  time: { path: "time/reports", sort_order: 9992 },
  payroll: { path: "payroll/reports", sort_order: 9993 },
  admin: { path: "admin/reports", sort_order: 9994 },
};

const MASTER_TAB_SEARCH_CONFIG = {
  generalMasters: {
    path: "/masters/general",
    breadcrumb: "General Masters",
    tabs: {
      todo: { label: "To Do" },
      kpi: { label: "KPIs" },
      kpiAllocation: { label: "KPI Allocations" },
      kpiEvaluation: { label: "KPI Evaluations" },
      accolades: { label: "Accolades" },
      skills: { label: "Skills" },
      careerLevels: { label: "Career Levels" },
      assets: { label: "Assets" },
      assetTypes: { label: "Asset Types" },
      benefits: { label: "Benefits" },
      bloodGroups: { label: "Blood Groups" },
      certifications: { label: "Certifications" },
      cities: { label: "Cities" },
      countries: { label: "Countries" },
      currencies: { label: "Currencies" },
      documents: { label: "Documents" },
      documentTypes: { label: "Document Types" },
      employeeTypes: { label: "Employee Types" },
      contractTypes: { label: "Employment Types" },
      institutes: { label: "Institutes" },
      languages: { label: "Languages" },
      occupationTypes: { label: "Occupation Types" },
      maritalStatuses: { label: "Marital Statuses" },
      newsLetters: { label: "News Letters" },
      relationships: { label: "Relationships" },
      religions: { label: "Religions" },
      qualifications: { label: "Qualifications" },
      states: { label: "States" },
      salutations: { label: "Salutations" },
      wordsOfWisdoms: { label: "Words of Wisdoms" },
    },
  },
  systemMasters: {
    path: "/masters/system",
    breadcrumb: "System Masters",
    tabs: {
      roles: { label: "Roles" },
      machines: { label: "Machines" },
      admsMachines: { label: "ADMS Machines", permissionKey: "machines" },
      machineType: { label: "Machine Types" },
      requestFlow: { label: "Request Flow" },
      users: { label: "Users" },
      schedulers: { label: "Schedulers" },
      logs: { label: "Activity Logs" },
    },
  },
};

export const normalizeNavKey = (value = "") => (
  String(value).replace(/[_\s./|-]/g, "").toLowerCase()
);

export const formatNavLabel = (value = "") => {
  const text = String(value).replace(/_/g, " ").replace(/-/g, " ").trim();
  if (!text) return "";

  const normalized = normalizeNavKey(text);
  if (normalized === "myspace") return "My Space";
  if (normalized === "whatsnew") return "What's New";
  if (normalized === "generalmasters") return "General Masters";
  if (normalized === "systemmasters") return "System Masters";
  if (normalized === "mastersgeneral") return "Masters - General";
  if (normalized === "mastersystem") return "Masters - System";
  if (normalized === "iprestrictions") return "IP Restrictions";

  return text
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bHr\b/g, "HR")
    .replace(/\bId\b/g, "ID")
    .replace(/\bKpis\b/g, "KPIs");
};

export const navItemPath = (itemOrSref) => {
  const sref = typeof itemOrSref === "string" ? itemOrSref : itemOrSref?.sref;
  const moduleKey = typeof itemOrSref === "string" ? "" : normalizeNavKey(itemOrSref?.module_key);

  if (moduleKey === "dashboard") return "/dashboard";
  if (moduleKey === "myspace") return "/my-space";

  // Keep cached pre-redesign navigation data compatible with the canonical self-profile page.
  if (moduleKey === "profile" && sref === "app.user.profile") return "/my-profile";

  let url = sref ? sref.replace(/^app\./, "").replace(/_/g, "-").replace(/\./g, "/") : "";

  if (url === "masters/general/accolades") url = url.replace(/\/accolades/g, "");
  if (url === "masters/system/users") url = url.replace(/\/users/g, "");
  if (url === "payrollmanagement/payrolls/index") url = url.replace(/\/index/g, "");
  if (url === "dashboard") return "/dashboard";

  return url ? `/${url}` : null;
};

const collapseSingleChildLevel = (items = []) => (
  items.map((item) => {
    const children = collapseSingleChildLevel(item.children || []);

    if (children.length === 1) {
      return {
        ...children[0],
        sort_order: item.sort_order,
      };
    }

    return {
      ...item,
      children,
    };
  })
);

const orderRoots = (items = []) => {
  const orderMap = new Map(TOP_LEVEL_NAV_ORDER.map((key, index) => [key, index]));

  return [...items].sort((first, second) => {
    const firstKey = normalizeNavKey(first.module_key || first.name);
    const secondKey = normalizeNavKey(second.module_key || second.name);
    const firstOrder = orderMap.has(firstKey) ? orderMap.get(firstKey) : 1000 + Number(first.sort_order || 0);
    const secondOrder = orderMap.has(secondKey) ? orderMap.get(secondKey) : 1000 + Number(second.sort_order || 0);

    return firstOrder - secondOrder;
  });
};

const appendScopedReportItems = (roots = []) => (
  roots.map((root) => {
    const rootKey = normalizeNavKey(root.module_key || root.name);
    const reportConfig = SCOPED_REPORT_NAV_ITEMS[rootKey];

    if (!reportConfig) return root;

    const children = root.children || [];
    const hasReportsItem = children.some((child) => navItemPath(child) === `/${reportConfig.path}`);

    if (hasReportsItem) return root;

    return {
      ...root,
      children: [
        ...children,
        {
          id: `scoped-reports-${rootKey}`,
          parentId: root.id,
          module_key: "reportsAll",
          name: "Reports",
          sref: `app.${reportConfig.path.replace(/\//g, ".")}`,
          icon: "fa fa-bar-chart",
          tag: null,
          tags: "reports",
          hasPermisson: true,
          sort_order: reportConfig.sort_order,
          super_admin: 0,
          hide_in_nav: 0,
          is_mobile: 0,
          synthetic: true,
          children: [],
        },
      ],
    };
  })
);

export const buildNavigationTree = (data = [], customPayItems = [], options = {}) => {
  const roots = [];
  const map = new Map();

  (data || []).forEach((item) => {
    map.set(item.id, { ...item, children: [] });
  });

  map.forEach((item) => {
    if (item.parentId === null) {
      roots.push(item);
      return;
    }

    const parent = map.get(item.parentId);
    if (!parent) return;

    parent.children.push(item);
    if (item.module_key === "customPayitem" && customPayItems?.length > 0) {
      customPayItems.forEach((payItem) => {
        item.children.push({
          ...payItem,
          sref: `app.payrollmanagement.custom_payitem.item/${payItem.name}/${payItem.id}`,
          module_key: `${payItem.name}_custom_payitem`,
          hasPermisson: true,
          icon: payItem.type === "deduction" ? "-" : payItem.type === "earning" ? "+" : "B.",
          parentId: item.id,
          tags: `payitem${payItem.name}`,
          children: [],
        });
      });
    }
  });

  const builtRoots = orderRoots(roots.map((root) => ({
    ...root,
    children: collapseSingleChildLevel(root.children || []),
  })));

  return options.includeScopedReports ? appendScopedReportItems(builtRoots) : builtRoots;
};

export const filterNavigationTree = (items, query, getLabel = (item) => item.name) => {
  const keyword = String(query || "").trim().toLowerCase();
  if (!keyword) return items;

  return (items || []).reduce((result, item) => {
    const children = filterNavigationTree(item.children || [], keyword, getLabel);
    const haystack = `${getLabel(item)} ${item.module_key || ""} ${item.name || ""}`.toLowerCase();

    if (haystack.includes(keyword) || children.length > 0) {
      result.push({ ...item, children });
    }

    return result;
  }, []);
};

export const findNavigationLineage = (items, predicate, lineage = []) => {
  for (const item of items || []) {
    const nextLineage = [...lineage, item];
    if (predicate(item)) return nextLineage;

    const childLineage = findNavigationLineage(item.children || [], predicate, nextLineage);
    if (childLineage) return childLineage;
  }

  return null;
};

export const findActiveLineage = (items, pathname, moduleKey = "") => {
  const normalizedPath = String(pathname || "/").replace(/\/+$/, "") || "/";
  let bestLineage = null;
  let bestLength = -1;

  const visit = (nodes, lineage = []) => {
    (nodes || []).forEach((item) => {
      const nextLineage = [...lineage, item];
      const path = navItemPath(item);
      const normalizedItemPath = path?.replace(/\/+$/, "") || path;

      if (
        normalizedItemPath
        && (
          normalizedPath === normalizedItemPath
          || (normalizedItemPath !== "/" && normalizedPath.startsWith(`${normalizedItemPath}/`))
        )
        && normalizedItemPath.length > bestLength
      ) {
        bestLineage = nextLineage;
        bestLength = normalizedItemPath.length;
      }

      visit(item.children || [], nextLineage);
    });
  };
  visit(items);

  if (bestLineage) return bestLineage;
  if (!moduleKey) return null;

  return findNavigationLineage(items, (item) => item.module_key === moduleKey);
};

export const firstNavigableLeaf = (item) => {
  if (!item) return null;

  for (const child of item.children || []) {
    const leaf = firstNavigableLeaf(child);
    if (leaf) return leaf;
  }

  return navItemPath(item) ? item : null;
};

export const shouldOpenWorkspaceDefault = (lineage, activeItem) => (
  !lineage?.[1] || !navItemPath(activeItem)
);

export const workspaceItemPath = (workspaceKey, item) => (
  normalizeNavKey(workspaceKey) === "myspace"
    && normalizeNavKey(item?.module_key) === "profile"
    ? "/my-space/profile"
    : navItemPath(item)
);

export const navigationItemLabel = (item) => formatNavLabel(item?.name || item?.module_key || "");

export const buildQuickMenuGroups = (root) => {
  if (!root?.children?.length) return [];

  const collectLeaves = (item, result = []) => {
    if (!item.children?.length) {
      if (navItemPath(item)) result.push(item);
      return result;
    }

    item.children.forEach((child) => collectLeaves(child, result));
    return result;
  };

  const directItems = [];
  const groups = [];

  root.children.forEach((child) => {
    if (!child.children?.length) {
      if (navItemPath(child)) directItems.push(child);
      return;
    }

    const items = collectLeaves(child);
    if (items.length) {
      groups.push({
        key: child.id || child.module_key,
        label: navigationItemLabel(child),
        items,
      });
    }
  });

  if (directItems.length) {
    groups.unshift({
      key: `${root.id || root.module_key}-quick-access`,
      label: "Quick Access",
      items: directItems,
    });
  }

  return groups;
};

// Flatten the permission-filtered navigation tree into a searchable list of every
// navigable page, each tagged with its parent breadcrumb (e.g. "My Space › My Team").
// Used by the global command search so it only ever surfaces pages the user can reach.
export const flattenNavigationPages = (tree = [], sourceItems = [], actionPermissions = {}) => {
  const pages = [];
  const seen = new Set();

  const addPage = (item, trail = []) => {
    const path = item.path || navItemPath(item);
    if (/^\/masters\/(general|system)\//.test(path || "")) return;
    if (!path || seen.has(path)) return;

    seen.add(path);
    pages.push({
      key: `${item.key ?? item.id ?? item.module_key ?? path}`,
      label: item.label || navigationItemLabel(item),
      path,
      breadcrumb: trail.join(" › "),
    });
  };

  if (sourceItems.length) {
    const byId = new Map(sourceItems.map((item) => [item.id, item]));
    const parentIds = new Set(sourceItems.map((item) => item.parentId).filter((id) => id !== null && id !== undefined));

    sourceItems.forEach((item) => {
      if (!parentIds.has(item.id)) return;

      const trail = [];
      let parent = byId.get(item.parentId);
      while (parent) {
        const parentLabel = navigationItemLabel(parent);
        if (parentLabel) trail.unshift(parentLabel);
        parent = byId.get(parent.parentId);
      }

      addPage(item, trail);
    });

    sourceItems.forEach((item) => {
      const parent = byId.get(item.parentId);
      const parentConfig = MASTER_TAB_SEARCH_CONFIG[parent?.module_key];
      if (!parentConfig) return;

      const tabConfig = parentConfig.tabs[item.module_key];
      if (!tabConfig) return;

      addPage({
        ...item,
        key: `${item.id ?? item.module_key}-tab`,
        label: tabConfig.label,
        path: `${parentConfig.path}?tab=${item.module_key}`,
      }, ["Masters", parentConfig.breadcrumb]);
    });

    Object.entries(MASTER_TAB_SEARCH_CONFIG.systemMasters.tabs).forEach(([tabKey, tabConfig]) => {
      if (!tabConfig.permissionKey) return;
      const sourceItem = sourceItems.find((item) => item.module_key === tabConfig.permissionKey);
      if (!sourceItem) return;

      addPage({
        ...sourceItem,
        key: `${tabKey}-tab`,
        label: tabConfig.label,
        path: `${MASTER_TAB_SEARCH_CONFIG.systemMasters.path}?tab=${tabKey}`,
      }, ["Masters", MASTER_TAB_SEARCH_CONFIG.systemMasters.breadcrumb]);
    });
  }

  Object.entries(MASTER_TAB_SEARCH_CONFIG).forEach(([, masterConfig]) => {
    Object.entries(masterConfig.tabs).forEach(([tabKey, tabConfig]) => {
      const permissionKey = tabConfig.permissionKey || tabKey;
      if (!actionPermissions[permissionKey] && !actionPermissions[`${permissionKey}_view`]) return;

      addPage({
        key: `${masterConfig.path}-${tabKey}`,
        label: tabConfig.label,
        path: `${masterConfig.path}?tab=${tabKey}`,
      }, ["Masters", masterConfig.breadcrumb]);
    });
  });

  const walk = (item, trail) => {
    const label = navigationItemLabel(item);
    addPage(item, trail);

    const nextTrail = label ? [...trail, label] : trail;
    (item.children || []).forEach((child) => walk(child, nextTrail));
  };

  (tree || []).forEach((root) => walk(root, []));

  return pages;
};

export const buildRailGroups = (tab) => {
  if (!tab?.children?.length) return [];

  const directItems = [];
  const groups = [];

  const collectLeaves = (item, result = []) => {
    if (!item.children?.length) {
      if (navItemPath(item)) result.push(item);
      return result;
    }

    item.children.forEach((child) => collectLeaves(child, result));
    return result;
  };

  tab.children.forEach((child) => {
    if (!child.children?.length) {
      if (navItemPath(child)) directItems.push(child);
      return;
    }

    const items = collectLeaves(child);
    if (items.length) {
      groups.push({
        key: child.id || child.module_key,
        label: navigationItemLabel(child),
        items,
      });
    }
  });

  if (directItems.length) {
    groups.unshift({
      key: `${tab.id || tab.module_key}-general`,
      label: navigationItemLabel(tab),
      items: directItems,
    });
  }

  return groups;
};
